/* eslint-disable max-lines, react/no-array-index-key, no-self-compare */

import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, Tooltip, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BackButton } from 'src/components/BackButton/BackButton';
import { BasePage } from 'src/components/BasePage';
import { Card } from 'src/components/Card/Card';
import { Form } from 'src/components/Form/Form';
import { TextField } from 'src/components/Form/TextField';
import { ValidationRules } from 'src/components/Form/ValidationRules';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { Router } from 'src/core/router/Router';
import { ProcessStoreProvider } from 'src/stores/ProcessStore/ProcessStore';
import { ConstraintViolationError } from 'src/utils/ErrorDataHelper';
import { FormHelper } from 'src/utils/FormHelper';
import styled from 'styled-components';

export const ApplicationsCreatePage = () => {
    const [isApproved, setIsApproved] = useState(false);
    const { t } = useTranslation();
    const store = ProcessStoreProvider.getInstance(
        'InitiateLoanApplicationProcess',
    );

    const handleSubmit = async (values: any) => {
        const tasks = await store.submit(values);

        if (!store.updateItemLoader.hasError && tasks.length > 0) {
            const [nextTask] = tasks;
            if (nextTask) {
                const link = EntityLinks.loanApplications.list?.();
                link && Router.navigate(`${link}/${nextTask.id}/choose-offers`);
            }
        }
    };

    const fields = FormHelper.getFieldDataItems(
        store.createItemLoader.errorData as ConstraintViolationError,
    );

    return (
        <BasePage pageName="ApplicationsCreatePage">
            <StyledHeaderContainer>
                <div>
                    <BackButton
                        to={
                            EntityLinks.loanApplications.list?.() ??
                            '/applications'
                        }
                    >
                        {t('Page.ApplicationsCreatePage.Back', 'Back')}
                    </BackButton>
                </div>
                <StyledHeaderTitle>
                    <div>
                        <Typography.Title level={1}>
                            {t(
                                'Page.ApplicationsCreatePage.Title',
                                'Create Application',
                            )}
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            {t(
                                'Page.ApplicationsCreatePage.Subtitle',
                                'Step 1: Verify your merchant account',
                            )}
                        </Typography.Text>
                    </div>
                </StyledHeaderTitle>
            </StyledHeaderContainer>

            <Form initialValues={{}} onFinish={handleSubmit} fields={fields}>
                <Card>
                    <TextField
                        name="secretKey"
                        label={
                            <StyledTextFieldLabel>
                                <span>
                                    {t(
                                        'Page.ApplicationsCreatePage.SecretKey',
                                        'Secret Key',
                                    )}
                                </span>
                                <Tooltip
                                    title={t(
                                        'Page.ApplicationsCreatePage.SecretKeyTooltip',
                                        'The key can be found on the Restaurant Portal, inside Branches/Partner Credentials.',
                                    )}
                                >
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            </StyledTextFieldLabel>
                        }
                        rules={[ValidationRules.required]}
                    />
                    <ConcentArea>
                        <Checkbox
                            checked={isApproved}
                            onChange={(e) => setIsApproved(e.target.checked)}
                        >
                            {t(
                                'Page.ApplicationsCreatePage.Consent',
                                'Consent to the terms and conditions',
                            )}
                        </Checkbox>
                    </ConcentArea>
                    <Button
                        htmlType="submit"
                        block
                        type="primary"
                        disabled={!isApproved}
                    >
                        {t(
                            'Page.ApplicationsCreatePage.Save',
                            'Continue to offers',
                        )}
                    </Button>
                </Card>
            </Form>
        </BasePage>
    );
};

const StyledHeaderContainer = styled.div`
    display: flex;
    gap: 16px;
    flex-direction: column;
`;

const StyledHeaderTitle = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    margin-bottom: 24px;

    h1 {
        flex: 1;
        margin: 0;
    }
`;

const ConcentArea = styled.div`
    position: relative;
    padding: 16px;
    background-color: var(--muted--bg-color);
    margin-bottom: 16px;
    border-radius: var(--border-radius);
`;

const StyledTextFieldLabel = styled.div`
    display: inline-flex;
    flex-direction: row;
    gap: 8px;
    margin-bottom: 8px;
`;
